/**
 * ミッション効果音の WebAudio 配管（plumbing）を集約する共有プリミティブ。
 *
 * 各音の「音の作り方（レシピ）」はミッション固有なので共有しないが、その土台と
 * なる「ミュート判定・AudioContext のライフサイクル（遅延生成・再利用）・例外
 * 処理」だけはここで一手に引き受ける。これにより `isAudioMuted()` 参照・
 * `new AudioContext()`・`try/catch` が本ファイルの 1 箇所だけになり、ミュートが
 * 全効果音に確実に効く（ガード漏れを防ぐ）。
 *
 * 効果音には 2 系統がある:
 * - 合成音（playSynth）: アセットの無い音を oscillator / noise で近似する。
 * - 実音源（playSample）: public/sounds/ の mp3 を 1 回鳴らす（ゴール音・発射音
 *   など、プロダクトが用意した実際の効果音）。重ね鳴り・連射のため
 *   AudioBufferSourceNode で再生し、デコード結果は URL 単位でキャッシュする。
 *
 * AudioContext はユーザー操作（再生・クリック）起点で生成されるため autoplay
 * 制限に掛からない。
 */
import { getAudioVolume, isAudioMuted } from '../audioMute'

/** モジュール共有の AudioContext。プレイをまたいで再利用する。 */
let sharedAudioContext: AudioContext | null = null

/** 合成音（playSynth）共通のマスター音量ゲイン。destination の手前に挟む。 */
let sharedMasterGain: GainNode | null = null

/** AudioContext を遅延生成して返す（プレイをまたいで再利用する）。 */
const ensureAudioContext = (): AudioContext =>
  (sharedAudioContext ??= new AudioContext())

/**
 * 共有 AudioContext を返す（HTMLAudio を Web Audio 経路へ載せるために再利用する）。
 * iOS は HTMLMediaElement.volume を無視するため、BGM/効果音も同じ AudioContext の
 * GainNode で音量制御し、合成音と単一の音声系統へ統一する。
 */
export const getSharedAudioContext = (): AudioContext => ensureAudioContext()

/**
 * 共有 AudioContext を resume する（既に生成済みのときのみ）。iOS はユーザー操作
 * 起点でないと suspended のままなので、タップ/キー等のジェスチャから呼ぶ。
 */
export const resumeSharedAudioContext = (): void => {
  void sharedAudioContext?.resume().catch(() => {
    // resume 失敗（未対応・状態不正）は無視する。次のジェスチャで再試行される。
  })
}

/**
 * 合成音をまとめて通すマスター音量ゲインを遅延生成する。playSynth でのみ使う
 * （playSample は個別ゲインへ直接マスター音量を掛けるため通さない）。
 */
const ensureMasterGain = (): GainNode => {
  const ctx = ensureAudioContext()
  if (!sharedMasterGain) {
    sharedMasterGain = ctx.createGain()
    sharedMasterGain.connect(ctx.destination)
  }
  return sharedMasterGain
}

/**
 * 音声合成を 1 回実行する。ミュート中は何もしない。AudioContext の遅延生成・
 * 再利用と例外の握り（warn のみ）をここで一手に引き受ける。render は
 * ctx・発音開始基準時刻 now・出力先 destination（マスター音量ゲイン）を受け取り、
 * oscillator / gain 等を組み立てて destination へ繋ぐ。
 * 失敗してもゲーム進行には影響させない（警告のみ）。
 */
export const playSynth = (
  render: (ctx: AudioContext, now: number, destination: AudioNode) => void,
): void => {
  if (isAudioMuted()) {
    return
  }
  try {
    const ctx = ensureAudioContext()
    // 合成音にもマスター音量を効かせる。再生時点の音量を master gain へ反映し、
    // render はこの master gain を出力先に使う（destination 直結にしない）。
    const master = ensureMasterGain()
    master.gain.value = getAudioVolume()
    render(ctx, ctx.currentTime, master)
  } catch (error) {
    console.warn('[preview] 効果音の再生に失敗しました', error)
  }
}

/** デコード済み音源（URL 単位でキャッシュし、同じ音の再フェッチを避ける）。 */
const sampleBufferCache = new Map<string, AudioBuffer>()

/** デコード進行中の読み込み（同 URL の同時多重フェッチを 1 本にまとめる）。 */
const samplePendingLoads = new Map<string, Promise<AudioBuffer | null>>()

/**
 * 音源 URL をフェッチしてデコードする。成功したらキャッシュへ載せ、失敗は
 * null を返して握る（warn のみ。ゲーム進行には影響させない）。
 */
const loadSample = (
  ctx: AudioContext,
  url: string,
): Promise<AudioBuffer | null> => {
  const cached = sampleBufferCache.get(url)
  if (cached) {
    return Promise.resolve(cached)
  }
  const pending = samplePendingLoads.get(url)
  if (pending) {
    return pending
  }
  const load = (async (): Promise<AudioBuffer | null> => {
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      const encoded = await response.arrayBuffer()
      const buffer = await ctx.decodeAudioData(encoded)
      sampleBufferCache.set(url, buffer)
      return buffer
    } catch (error) {
      console.warn('[preview] 効果音の読み込みに失敗しました', error)
      return null
    } finally {
      samplePendingLoads.delete(url)
    }
  })()
  samplePendingLoads.set(url, load)
  return load
}

/**
 * public/sounds/ の音源を 1 回鳴らす。ミュート中は何もしない。初回はフェッチ・
 * デコードしてキャッシュし、以降は即座に再生する（連射・重ね鳴りに対応）。
 * 読み込み完了までにミュート/中断（signal）へ切り替わった場合は鳴らさない。
 * 失敗してもゲーム進行には影響させない（警告のみ）。
 *
 * signal を渡すと、プレイ停止・再実行（ビヘイビアの ctx.signal の abort）で
 * 再生中の音も止める。実機の Sound（BGM・効果音）はプレイ停止で止まるため、
 * 合成ジングルより長い実音源（数秒）が停止後に鳴り残るのを防ぐ。
 *
 * @param url 音源 URL（例: '/sounds/sfx-goal-success.mp3'）
 * @param volume 音量（0..1）
 * @param signal 中断シグナル（省略時は鳴らしっぱなしの fire-and-forget）
 */
export const playSample = (
  url: string,
  volume: number,
  signal?: AbortSignal,
): void => {
  if (signal?.aborted || isAudioMuted()) {
    return
  }
  try {
    const ctx = ensureAudioContext()
    void loadSample(ctx, url)
      .then((buffer) => {
        if (!buffer || signal?.aborted || isAudioMuted()) {
          return
        }
        const source = ctx.createBufferSource()
        source.buffer = buffer
        const gain = ctx.createGain()
        // 素材ごとの音量にプレビューのマスター音量（音量スライダー）を掛ける。
        gain.gain.value = volume * getAudioVolume()
        source.connect(gain)
        gain.connect(ctx.destination)
        // 後始末（idempotent）: abort リスナを外し、ノードを切り離す。自然終了・
        // 中断・start 失敗のいずれの経路でも 1 回だけ実行し、セッション中の
        // リスナ/ノードの蓄積を防ぐ。
        let cleaned = false
        function cleanup(): void {
          if (cleaned) {
            return
          }
          cleaned = true
          signal?.removeEventListener('abort', onAbort)
          source.onended = null
          source.disconnect()
          gain.disconnect()
        }
        // 中断（停止・再実行）で再生中の音を止める。
        function onAbort(): void {
          try {
            source.stop()
          } catch {
            // すでに停止/終了済み。
          }
          cleanup()
        }
        signal?.addEventListener('abort', onAbort, { once: true })
        source.onended = cleanup
        try {
          source.start()
        } catch (error) {
          cleanup()
          throw error
        }
      })
      .catch((error: unknown) => {
        console.warn('[preview] 効果音の再生に失敗しました', error)
      })
  } catch (error) {
    console.warn('[preview] 効果音の再生に失敗しました', error)
  }
}
